import { useEffect, useState, useCallback, useMemo, Fragment } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Search, Play, Eye, Check, X, ExternalLink, ShieldCheck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  not_started: 'bg-slate-100 text-slate-700',
  auto_extracted: 'bg-emerald-100 text-emerald-800',
  needs_review: 'bg-amber-100 text-amber-800',
  low_confidence: 'bg-orange-100 text-orange-800',
  failed: 'bg-rose-100 text-rose-800',
  verified: 'bg-purple-100 text-purple-800',
  skipped: 'bg-slate-100 text-slate-500',
};

const STATUS_LABEL = {
  not_started: 'Niet gestart',
  auto_extracted: 'Auto extracted',
  needs_review: 'Needs review',
  low_confidence: 'Low confidence',
  failed: 'Failed',
  verified: 'Verified',
  skipped: 'Skipped',
};

function ConfidenceBar({ value }) {
  const pct = Math.max(0, Math.min(1, value || 0)) * 100;
  let color = 'bg-rose-500';
  if (value >= 0.80) color = 'bg-emerald-500';
  else if (value >= 0.40) color = 'bg-amber-500';
  return (
    <div className="flex items-center gap-2" data-testid="confidence-bar">
      <div className="w-20 h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs tabular-nums text-muted-foreground">{pct.toFixed(0)}%</span>
    </div>
  );
}

function FindingCard({ finding, onAccept, accepting, kind }) {
  const flags = finding.flags || [];
  const hasMismatch = flags.includes('domain_mismatch');
  const hasPlaceholder = flags.includes('placeholder') || flags.includes('blacklisted');
  const hasRoleFlag = finding.role_based_email_flag === true;
  return (
    <div className="border border-border rounded-lg p-3 bg-white space-y-2" data-testid={`finding-${kind}-${finding.candidate_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm break-all">{finding.display_value || finding.value}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            via <span className="font-medium">{finding.extraction_method}</span>
          </div>
        </div>
        <ConfidenceBar value={finding.confidence} />
      </div>
      {flags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {flags.map(f => (
            <span
              key={f}
              className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                f === 'domain_mismatch' || f === 'placeholder' || f === 'blacklisted' || f === 'role_based' || f.startsWith('role:')
                  ? 'bg-rose-100 text-rose-700'
                  : f === 'free_email_provider'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-slate-100 text-slate-600'
              }`}
              data-testid={`flag-${f}`}
            >
              {f}
            </span>
          ))}
        </div>
      )}
      {finding.email_role_type && finding.email_role_type !== 'commercial' && finding.email_role_type !== 'generic' && finding.email_role_type !== 'unknown' && (
        <div className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5" data-testid="role-warning">
          ⚠ Niet-commercieel adres ({finding.email_role_type}) — handmatige bevestiging vereist vóór outreach
        </div>
      )}
      {finding.source_url && (
        <a
          href={finding.source_url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-[11px] text-sky-700 hover:underline flex items-center gap-1 break-all"
          data-testid="finding-source-url"
        >
          <ExternalLink className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">{finding.source_url}</span>
        </a>
      )}
      {finding.evidence_snippet && (
        <div className="text-[11px] bg-slate-50 border border-slate-200 rounded p-1.5 font-mono text-slate-700 break-all">
          {finding.evidence_snippet}
        </div>
      )}
      <button
        onClick={() => onAccept(finding.candidate_id)}
        disabled={accepting || hasPlaceholder || hasRoleFlag}
        className={`w-full text-xs px-2 py-1.5 rounded font-medium flex items-center justify-center gap-1 ${
          hasMismatch || hasRoleFlag
            ? 'bg-amber-600 text-white hover:bg-amber-700'
            : 'bg-emerald-600 text-white hover:bg-emerald-700'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid={`accept-${kind}-btn`}
        title={
          hasPlaceholder
            ? 'Placeholder/blacklisted, niet acceptabel'
            : hasRoleFlag
            ? `Niet-commercieel adres (${finding.email_role_type}) — gebruik UI nooit voor outreach`
            : 'Kopieer naar canonical veld'
        }
      >
        {accepting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        Accept {kind}
        {(hasMismatch || hasRoleFlag) && <AlertTriangle className="w-3 h-3" />}
      </button>
    </div>
  );
}

function SafetyChips({ p }) {
  const items = [
    { k: 'approved', v: p.approved === true ? 'true' : 'false', ok: p.approved !== true },
    { k: 'visible', v: p.visible === true ? 'true' : 'false', ok: p.visible !== true },
    { k: 'outreach', v: p.outreach_status || 'not_sent', ok: (p.outreach_status || 'not_sent') === 'not_sent' },
    { k: 'agreement', v: p.agreement_status || 'pending', ok: (p.agreement_status || 'pending') === 'pending' },
  ];
  return (
    <div className="flex flex-wrap gap-1" data-testid="safety-chips">
      {items.map(it => (
        <span
          key={it.k}
          className={`text-[10px] px-1.5 py-0.5 rounded-full ${
            it.ok ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
          }`}
          data-testid={`safety-${it.k}`}
        >
          {it.ok && <ShieldCheck className="w-2.5 h-2.5 inline mr-0.5" />}
          {it.k}={it.v}
        </span>
      ))}
    </div>
  );
}

export default function ProviderEnrichmentTab({ token }) {
  const [queue, setQueue] = useState([]);
  const [thresholds, setThresholds] = useState({ auto_extract: 0.8, needs_review: 0.4 });
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: '', category: '', region: '', search: '' });
  const [busyId, setBusyId] = useState(null);
  const [findingsByProvider, setFindingsByProvider] = useState({}); // provider_id → full doc with findings
  const [expandedId, setExpandedId] = useState(null);
  const authH = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = filters.status ? `?limit=200&enrichment_status=${encodeURIComponent(filters.status)}` : '?limit=200';
      const r = await axios.get(`${API}/admin/provider-enrichment/preview-queue${params}`, { headers: authH });
      setQueue(r.data.queue || []);
      setThresholds(r.data.thresholds || {});
      setTotalCandidates(r.data.total_candidates || 0);
    } catch (e) {
      toast.error('Kon queue niet laden');
    } finally {
      setLoading(false);
    }
  }, [authH, filters.status]);

  const loadReviewList = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/provider-enrichment/review-list?limit=200`, { headers: authH });
      const map = {};
      (r.data.providers || []).forEach(p => { map[p.provider_id] = p; });
      setFindingsByProvider(map);
    } catch (e) {
      // silent
    }
  }, [authH]);

  useEffect(() => { loadQueue(); loadReviewList(); }, [loadQueue, loadReviewList]);

  const runOne = async (provider_id, dryRun) => {
    setBusyId(provider_id + (dryRun ? '_dry' : '_real'));
    try {
      const r = await axios.post(`${API}/admin/provider-enrichment/run-one/${provider_id}`,
        { dry_run: dryRun }, { headers: authH });
      const summary = r.data.summary || {};
      toast.success(`${dryRun ? 'Dry-run' : 'Run'} klaar: ${summary.enrichment_status} (email=${summary.best_email_confidence}, phone=${summary.best_phone_confidence})`);
      // Inject the just-fetched findings into the per-provider cache so
      // dry-run findings appear inline without an extra DB round-trip.
      setFindingsByProvider(prev => ({
        ...prev,
        [provider_id]: {
          ...(prev[provider_id] || {}),
          provider_id,
          enrichment_status: summary.enrichment_status,
          enrichment_summary: summary,
          contact_page_url: r.data.contact_page_url,
          kvk_search_url: r.data.kvk_search_url,
          emails: r.data.findings?.emails || [],
          phones: r.data.findings?.phones || [],
          kvks: r.data.findings?.kvks || [],
        },
      }));
      setExpandedId(provider_id);
      if (!dryRun) {
        loadQueue();
        loadReviewList();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Fout: ${detail}`);
    } finally {
      setBusyId(null);
    }
  };

  const acceptCandidate = async (provider_id, kind, candidate_id) => {
    setBusyId(`${provider_id}_${kind}_accept`);
    try {
      const body = {};
      if (kind === 'email') body.email_candidate_id = candidate_id;
      if (kind === 'phone') body.phone_candidate_id = candidate_id;
      const r = await axios.post(`${API}/admin/provider-enrichment/accept/${provider_id}`,
        body, { headers: authH });
      toast.success(`${kind} geaccepteerd: ${r.data.canonical?.[kind]}`);
      loadReviewList();
      loadQueue();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Accept fout: ${detail}`);
    } finally {
      setBusyId(null);
    }
  };

  const skipProvider = async (provider_id) => {
    const reason = window.prompt('Reden om over te slaan (bv. "company_closed", "wrong_match"):', 'manual_skip');
    if (!reason) return;
    setBusyId(`${provider_id}_skip`);
    try {
      await axios.post(`${API}/admin/provider-enrichment/skip/${provider_id}`,
        { reason }, { headers: authH });
      toast.success('Overgeslagen');
      loadQueue();
      loadReviewList();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Skip fout: ${detail}`);
    } finally {
      setBusyId(null);
    }
  };

  // Client-side filter on category / region / search (queue is already capped at 200)
  const filteredQueue = useMemo(() => {
    return queue.filter(p => {
      if (filters.category && !(p.categories || []).includes(filters.category)) return false;
      if (filters.region && !(p.postcode_prefixes || []).map(String).includes(String(filters.region))) return false;
      if (filters.search) {
        const s = filters.search.toLowerCase();
        const haystack = `${p.company_name || ''} ${p.website || ''} ${p.city || ''}`.toLowerCase();
        if (!haystack.includes(s)) return false;
      }
      return true;
    });
  }, [queue, filters.category, filters.region, filters.search]);

  // Build distinct lists for filter dropdowns
  const distinctCategories = useMemo(() => {
    const s = new Set();
    queue.forEach(p => (p.categories || []).forEach(c => s.add(c)));
    return [...s].sort();
  }, [queue]);
  const distinctRegions = useMemo(() => {
    const s = new Set();
    queue.forEach(p => (p.postcode_prefixes || []).forEach(r => s.add(String(r))));
    return [...s].sort();
  }, [queue]);

  return (
    <div className="space-y-4" data-testid="provider-enrichment-tab">
      {/* Safety banner — always visible to reinforce hard rules */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-900 flex items-start gap-2" data-testid="enrichment-safety-banner">
        <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <strong>Veilige modus:</strong> deze tab muteert NOOIT <code>approved</code>, <code>visible</code>, <code>outreach_status</code> of <code>agreement_status</code>.
          Canonical email/phone wordt alleen gevuld via expliciete Accept-klik. Geen LLM, geen outreach, geen klantdata.
        </div>
      </div>

      {/* Header + filters */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-medium" data-testid="enrichment-title">Provider Enrichment</h2>
          <span className="text-xs text-muted-foreground" data-testid="enrichment-total">
            {totalCandidates} enrichable kandidaten · drempels: ≥{thresholds.auto_extract} auto · ≥{thresholds.needs_review} review
          </span>
        </div>
        <button
          onClick={() => { loadQueue(); loadReviewList(); }}
          disabled={loading}
          className="text-xs px-2 py-1 rounded border border-border hover:bg-muted flex items-center gap-1"
          data-testid="enrichment-refresh-btn"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 items-center" data-testid="enrichment-filters">
        <div className="relative">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="zoek bedrijf / website / stad"
            value={filters.search}
            onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
            className="text-xs pl-7 pr-2 py-1 border border-border rounded w-56"
            data-testid="filter-search"
          />
        </div>
        <select
          value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
          className="text-xs px-2 py-1 border border-border rounded"
          data-testid="filter-status"
        >
          <option value="">alle statussen</option>
          {Object.keys(STATUS_LABEL).map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
        <select
          value={filters.category}
          onChange={e => setFilters(f => ({ ...f, category: e.target.value }))}
          className="text-xs px-2 py-1 border border-border rounded"
          data-testid="filter-category"
        >
          <option value="">alle categorieën</option>
          {distinctCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          value={filters.region}
          onChange={e => setFilters(f => ({ ...f, region: e.target.value }))}
          className="text-xs px-2 py-1 border border-border rounded"
          data-testid="filter-region"
        >
          <option value="">alle regio's</option>
          {distinctRegions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Queue table */}
      <div className="border border-border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="enrichment-queue-table">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Bedrijf</th>
              <th className="text-left px-3 py-2 font-medium">Cat · Regio</th>
              <th className="text-left px-3 py-2 font-medium">Prio</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Email / Phone</th>
              <th className="text-left px-3 py-2 font-medium">Acties</th>
            </tr>
          </thead>
          <tbody>
            {filteredQueue.map(p => {
              const findings = findingsByProvider[p.provider_id];
              const status = findings?.enrichment_status || p.enrichment_status || 'not_started';
              const isExpanded = expandedId === p.provider_id;
              return (
                <Fragment key={p.provider_id}>
                  <tr className="border-t border-border hover:bg-muted/20" data-testid={`row-${p.provider_id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium" data-testid="row-company-name">{p.company_name}</div>
                      <div className="text-[11px] text-muted-foreground">{p.city || '—'}</div>
                      {p.website && (
                        <a href={p.website} target="_blank" rel="noreferrer noopener" className="text-[11px] text-sky-700 hover:underline flex items-center gap-1 mt-0.5">
                          <ExternalLink className="w-3 h-3" />
                          <span className="truncate max-w-[200px]">{p.website}</span>
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div>{(p.categories || []).join(', ') || '—'}</div>
                      <div className="text-muted-foreground">PC: {(p.postcode_prefixes || []).join(', ') || '—'}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="text-sm font-mono">{p.priority_score}</div>
                      <div className="text-[10px] text-muted-foreground">{(p.priority_reasons || []).slice(0, 2).join(' · ')}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[status] || 'bg-slate-100'}`} data-testid="status-badge">
                        {STATUS_LABEL[status] || status}
                      </span>
                      <div className="mt-1"><SafetyChips p={p} /></div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className={p.has_email ? 'text-emerald-700' : 'text-muted-foreground'} data-testid="canonical-email-state">
                        ✉ {p.has_email ? 'gezet' : 'leeg'}
                      </div>
                      <div className={p.has_phone ? 'text-emerald-700' : 'text-muted-foreground'} data-testid="canonical-phone-state">
                        ☎ {p.has_phone ? 'gezet' : 'leeg'}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <button
                          onClick={() => runOne(p.provider_id, true)}
                          disabled={!!busyId}
                          className="text-[11px] px-2 py-1 rounded bg-sky-100 text-sky-800 hover:bg-sky-200 disabled:opacity-50 flex items-center gap-1"
                          data-testid="run-dryrun-btn"
                        >
                          {busyId === p.provider_id + '_dry' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                          Dry-run
                        </button>
                        <button
                          onClick={() => runOne(p.provider_id, false)}
                          disabled={!!busyId}
                          className="text-[11px] px-2 py-1 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200 disabled:opacity-50 flex items-center gap-1"
                          data-testid="run-real-btn"
                        >
                          {busyId === p.provider_id + '_real' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                          Run
                        </button>
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : p.provider_id)}
                          className="text-[11px] px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
                          data-testid="toggle-findings-btn"
                        >
                          {isExpanded ? 'Verberg' : 'Toon'} findings
                        </button>
                        <button
                          onClick={() => skipProvider(p.provider_id)}
                          disabled={!!busyId}
                          className="text-[11px] px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-50 flex items-center gap-1"
                          data-testid="skip-btn"
                        >
                          <X className="w-3 h-3" /> Skip
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-50/60" data-testid={`findings-${p.provider_id}`}>
                      <td colSpan={6} className="px-3 py-3">
                        {!findings && (
                          <div className="text-xs text-muted-foreground">Nog geen findings. Klik "Dry-run" of "Run".</div>
                        )}
                        {findings && (
                          <div className="space-y-3">
                            {(findings.contact_page_url || findings.kvk_search_url) && (
                              <div className="flex flex-wrap gap-3 text-xs">
                                {findings.contact_page_url && (
                                  <a href={findings.contact_page_url} target="_blank" rel="noreferrer noopener" className="text-sky-700 hover:underline flex items-center gap-1">
                                    <ExternalLink className="w-3 h-3" /> Contactpagina
                                  </a>
                                )}
                                {findings.kvk_search_url && (
                                  <a href={findings.kvk_search_url} target="_blank" rel="noreferrer noopener" className="text-sky-700 hover:underline flex items-center gap-1">
                                    <ExternalLink className="w-3 h-3" /> KvK zoeken
                                  </a>
                                )}
                              </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <div className="text-xs font-medium mb-1.5 text-muted-foreground">Email kandidaten ({(findings.emails || []).length})</div>
                                <div className="space-y-2">
                                  {(findings.emails || []).length === 0 && (
                                    <div className="text-xs text-muted-foreground italic">Geen email gevonden</div>
                                  )}
                                  {(findings.emails || []).map(e => (
                                    <FindingCard
                                      key={e.candidate_id}
                                      finding={e}
                                      kind="email"
                                      accepting={busyId === `${p.provider_id}_email_accept`}
                                      onAccept={cid => acceptCandidate(p.provider_id, 'email', cid)}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs font-medium mb-1.5 text-muted-foreground">Telefoon kandidaten ({(findings.phones || []).length})</div>
                                <div className="space-y-2">
                                  {(findings.phones || []).length === 0 && (
                                    <div className="text-xs text-muted-foreground italic">Geen telefoon gevonden</div>
                                  )}
                                  {(findings.phones || []).map(ph => (
                                    <FindingCard
                                      key={ph.candidate_id}
                                      finding={ph}
                                      kind="phone"
                                      accepting={busyId === `${p.provider_id}_phone_accept`}
                                      onAccept={cid => acceptCandidate(p.provider_id, 'phone', cid)}
                                    />
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {filteredQueue.length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground" data-testid="enrichment-empty">
                  Geen providers in queue voor deze filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
